import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, BehaviorSubject, shareReplay, switchMap, timer, map } from 'rxjs';
import { environment } from '../../../environments/environment';

export interface CachedData<T> {
  data: T;
  timestamp: number;
  loading: boolean;
  error?: any;
}

@Injectable({
  providedIn: 'root'
})
export class PerformanceDataService {
  private readonly CACHE_DURATION = 5 * 60 * 1000; // 5 minutes
  
  // Navigation tabs cache
  private navigationTabsSubject = new BehaviorSubject<CachedData<any[]>>({
    data: [],
    timestamp: 0,
    loading: false
  });
  
  // Categories cache
  private categoriesSubject = new BehaviorSubject<CachedData<any[]>>({
    data: [],
    timestamp: 0,
    loading: false
  });
  
  // Mega menu images cache
  private megaMenuImagesSubject = new BehaviorSubject<CachedData<any[]>>({
    data: [],
    timestamp: 0,
    loading: false
  });

  constructor(private http: HttpClient) {}

  // Navigation Tabs with caching
  getNavigationTabs(forceRefresh = false): Observable<any[]> {
    const cached = this.navigationTabsSubject.value;
    const isExpired = Date.now() - cached.timestamp > this.CACHE_DURATION;
    
    if (cached.data.length && !isExpired && !forceRefresh) {
      return this.navigationTabsSubject.pipe(map(cache => cache.data));
    }

    if (cached.loading) {
      return this.navigationTabsSubject.pipe(
        map(cache => cache.data)
      );
    }

    // Set loading state
    this.navigationTabsSubject.next({
      ...cached,
      loading: true,
      error: undefined
    });

    return this.http.get<any[]>(`${environment.baseUrl}/navigation-tabs/main-with-children`).pipe(
      shareReplay(1),
      map(data => {
        this.navigationTabsSubject.next({
          data,
          timestamp: Date.now(),
          loading: false
        });
        return data;
      })
    );
  }

  // Categories with caching
  getCategories(forceRefresh = false): Observable<any[]> {
    const cached = this.categoriesSubject.value;
    const isExpired = Date.now() - cached.timestamp > this.CACHE_DURATION;
    
    if (cached.data.length && !isExpired && !forceRefresh) {
      return this.categoriesSubject.pipe(map(cache => cache.data));
    }

    if (cached.loading) {
      return this.categoriesSubject.pipe(
        map(cache => cache.data)
      );
    }

    // Set loading state
    this.categoriesSubject.next({
      ...cached,
      loading: true,
      error: undefined
    });

    return this.http.post<any>(`${environment.baseUrl}/products__category_product/categories-by-level?page=1&limit=100`, {}).pipe(
      shareReplay(1),
      map(response => {
        const data = response.data || [];
        this.categoriesSubject.next({
          data,
          timestamp: Date.now(),
          loading: false
        });
        return data;
      })
    );
  }

  // Mega menu images with caching
  getMegaMenuImages(forceRefresh = false): Observable<any[]> {
    const cached = this.megaMenuImagesSubject.value;
    const isExpired = Date.now() - cached.timestamp > this.CACHE_DURATION;
    
    if (cached.data.length && !isExpired && !forceRefresh) {
      return this.megaMenuImagesSubject.pipe(map(cache => cache.data));
    }

    if (cached.loading) {
      return this.megaMenuImagesSubject.pipe(
        map(cache => cache.data)
      );
    }

    // Set loading state
    this.megaMenuImagesSubject.next({
      ...cached,
      loading: true,
      error: undefined
    });

    return this.http.get<any[]>(`${environment.baseUrl}/mega-menu/images/active`).pipe(
      shareReplay(1),
      map(data => {
        this.megaMenuImagesSubject.next({
          data,
          timestamp: Date.now(),
          loading: false
        });
        return data;
      })
    );
  }

  // REMOVED: Redis endpoint no longer exists
  // Use individual cached methods instead
  getInitialData(): Observable<{
    navigationTabs: any[];
    categories: any[];
    megaMenuImages: any[];
  }> {
    // Fallback to individual API calls with client-side combination
    return new Observable(observer => {
      const result: {
        navigationTabs: any[];
        categories: any[];
        megaMenuImages: any[];
      } = {
        navigationTabs: [],
        categories: [],
        megaMenuImages: []
      };
      
      let completed = 0;
      const total = 3;
      
      const checkComplete = () => {
        completed++;
        if (completed === total) {
          observer.next(result);
          observer.complete();
        }
      };
      
      // Load navigation tabs
      this.getNavigationTabs().subscribe({
        next: (data) => {
          result.navigationTabs = data;
          checkComplete();
        },
        error: () => checkComplete()
      });
      
      // Load categories
      this.getCategories().subscribe({
        next: (data) => {
          result.categories = data;
          checkComplete();
        },
        error: () => checkComplete()
      });
      
      // Load mega menu images
      this.getMegaMenuImages().subscribe({
        next: (data) => {
          result.megaMenuImages = data;
          checkComplete();
        },
        error: () => checkComplete()
      });
    });
  }

  // Legacy method - keeping for compatibility but using new endpoint internally
  getInitialDataLegacy(): Observable<{
    navigationTabs: any[];
    categories: any[];
    megaMenuImages: any[];
  }> {
    // For now, just use the new method
    return this.getInitialData();
  }

  // Clear all cache
  clearCache(): void {
    this.navigationTabsSubject.next({
      data: [],
      timestamp: 0,
      loading: false
    });
    this.categoriesSubject.next({
      data: [],
      timestamp: 0,
      loading: false
    });
    this.megaMenuImagesSubject.next({
      data: [],
      timestamp: 0,
      loading: false
    });
  }

  // Get loading states
  getLoadingStates(): Observable<{
    navigationTabs: boolean;
    categories: boolean;
    megaMenuImages: boolean;
  }> {
    return new Observable(observer => {
      const subscription = timer(0, 100).subscribe(() => {
        observer.next({
          navigationTabs: this.navigationTabsSubject.value.loading,
          categories: this.categoriesSubject.value.loading,
          megaMenuImages: this.megaMenuImagesSubject.value.loading
        });
      });
      
      return () => subscription.unsubscribe();
    });
  }
}
